import React, { useMemo } from "react";
import { create } from "zustand";
import { EditorContext, useCodebaseStore } from "./codebaseStore";
import { PRDDesignSuggestions } from "@/components/AI/PRD/PRD";
import { useAiActionStore, useCurrentConvo } from "./aiActionStore";
import { toast } from "sonner";
import { parseMessageContentBlocks } from "@/components/AI/chat/utils/messages";
import { Message } from "./supabaseStore";
import {
  getAiMessageFromErrors,
  useJoinedErrors,
} from "@/components/Canvas/hooks/useStoryboardErrors";
import { UploadedFile } from "@/utils/types";

export const FULL_CODEBASE_CONSTANT = "___FULL_CODEBASE_DESCRIPTION___";

export interface Suggestion {
  short_title: string;
  detailed_description: string;
}

export interface ChatContextCodeSnippet {
  filename: string;
  startLine: number;
  startCol: number;
  endLine: number;
  endCol: number;
  contents: string;
  editorContext: EditorContext;

  engSpecPrId?: string;
  engSpecCommitId?: string;
  engSpecFileDiffId?: string;
}

export interface ManualAssistantMessage {
  id: string;
  text?: string;

  progressItems?: {
    title: string;
    succeeded?: boolean;
    failed?: boolean;
    loading?: boolean;
  }[];

  afterText?: string;
}

export interface SetSendChatMessageParams {
  sendChatMessage: string;
  sendChatMessageFiles?: UploadedFile[];
  refreshAfterResponse?: boolean;
  includeErrorsIfAny?: boolean;
  isFreePrompt?: boolean;
  isFixWithAi?: boolean;
  resetToggles?: "all" | "reasoning_and_search" | null;
}

export interface AiContextStore {
  allFiles: string[];
  fileTree: string;
  setAllFiles: (files: string[]) => void;
  setFileTree: (fileTree: string) => void;

  // Eng Spec
  engSpecOpenedFileId: string;
  engSpecOpenedFileIsCommand: boolean;
  setEngSpecOpenedFile: (fileId: string, isCommand: boolean) => void;

  // For chat context
  chatContextCodeSnippet: ChatContextCodeSnippet;
  chatContextDiffCodeSnippet: ChatContextCodeSnippet;

  setChatContext: (
    codeSnippet?: ChatContextCodeSnippet,
    diffCodeSnippet?: ChatContextCodeSnippet
  ) => void;

  resetAiContextStore: () => void;

  // Design suggestions: these are usually stored on the featurePRD DB object
  // but when streaming data, store them in a separate state so it doesn't
  // cause a ton of re-renders
  streamedDesignSuggestions: PRDDesignSuggestions;
  setStreamedDesignSuggestions: (suggestions: PRDDesignSuggestions) => void;

  generatingDesignSuggestions: boolean;
  setGeneratingDesignSuggestions: (generating: boolean) => void;

  generatingStoryboardIds: Set<string>;
  setGeneratingStoryboardIds: (storyboardIds: Set<string>) => void;

  aiChatEngSpecOpen: boolean;
  aiChatEngSpecMessageId: string;
  aiChatEngSpecBlockIndex: number;
  openAiChatEngSpec: (blockIndex: number, messageId: string) => void;
  closeAiChatEngSpec: () => void;

  // Code Diff Panel (for applyFileEditsTool results)
  codeDiffPanelOpen: boolean;
  codeDiffMessageId: string | null;
  codeDiffToolCallId: string | null;
  openCodeDiffPanel: (toolCallId: string, messageId: string) => void;
  closeCodeDiffPanel: () => void;

  aiChatSuggestions: Suggestion[];
  setAiChatSuggestions: (suggestions: Suggestion[]) => void;

  triggerGenerateWireframes: boolean;
  setTriggerGenerateWireframes: (trigger: boolean) => void;

  // Trigger the chat from anywhere in the app
  sendChatMessage: string;
  sendChatMessageFiles?: UploadedFile[];
  refreshAfterResponse: boolean; // If set to true, refreshes all storyboards after a response
  includeErrorsIfAny: boolean; // If set to true, includes errors in the chat message
  isFreePrompt: boolean; // If set to true, the prompt is free
  setSendChatMessage: (params: SetSendChatMessageParams) => boolean;
  resetToggles: (resetToggles?: "all" | "reasoning_and_search" | null) => void;

  // For tracking fix with AI messages
  numFixWithAiMessages: number;
  setNumFixWithAiMessages: (num: number) => void;

  // Show an extra assistant message on the end of the conversation
  // If set cannot change conversation or use chat until cleared
  tempAssistantMessage: ManualAssistantMessage;
  setTempAssistantMessage: (message: ManualAssistantMessage) => void;

  manualMessageToAdd: ManualAssistantMessage;
  setAddManualMessage: (message: ManualAssistantMessage) => void;

  chatOnlyMode: boolean;
  setChatOnlyMode: (enabled: boolean) => void;

  setUseSearch: (enabled: boolean) => void;
  useSearch: boolean;

  setUseReasoning: (enabled: boolean) => void;
  useReasoning: boolean;
}

export const useAiContextStore = create<AiContextStore>((set, get) => ({
  allFiles: [],
  fileTree: "",

  setAllFiles: (files: string[]) => set({ allFiles: files }),
  setFileTree: (fileTree: string) => set({ fileTree: fileTree }),

  // @ts-expect-error TS2322
  engSpecOpenedFileId: null,
  engSpecOpenedFileIsCommand: false,
  setEngSpecOpenedFile: (fileId: string, isCommand: boolean) => {
    set({ engSpecOpenedFileId: fileId, engSpecOpenedFileIsCommand: isCommand });
  },

  // @ts-expect-error TS2322
  chatContextCodeSnippet: null,
  // @ts-expect-error TS2322
  chatContextDiffCodeSnippet: null,
  chatOnlyMode: false,
  useReasoning: false,
  useSearch: false,

  setChatContext: (
    chatContextCodeSnippet?: ChatContextCodeSnippet,
    chatContextDiffCodeSnippet?: ChatContextCodeSnippet
  ) => {
    set({
      // @ts-expect-error TS2322
      chatContextCodeSnippet: chatContextCodeSnippet || null,
      // @ts-expect-error TS2322
      chatContextDiffCodeSnippet: chatContextDiffCodeSnippet || null,
    });
  },

  resetAiContextStore: () => {
    set({
      allFiles: [],
      fileTree: "",
      // @ts-expect-error TS2322
      streamedDesignSuggestions: null,
      generatingDesignSuggestions: false,
      aiChatEngSpecOpen: false,
      // @ts-expect-error TS2322
      generatingStoryboardIds: null,
      // @ts-expect-error TS2322
      aiChatEngSpecBlockIndex: null,
      // @ts-expect-error TS2322
      aiChatEngSpecMessageId: null,
      // @ts-expect-error TS2322
      engSpecOpenedFileId: null,
      // @ts-expect-error TS2322
      chatContextCodeSnippet: null,
      // @ts-expect-error TS2322
      chatContextDiffCodeSnippet: null,
      sendChatMessage: "",
      sendChatMessageFiles: undefined,
      refreshAfterResponse: false,
      includeErrorsIfAny: false,
      isFreePrompt: false,
      triggerGenerateWireframes: false,
      // @ts-expect-error TS2322
      tempAssistantMessage: null,
      // @ts-expect-error TS2322
      manualMessageToAdd: null,
      numFixWithAiMessages: 0,
      chatOnlyMode: false,
      useReasoning: false,
      useSearch: false,
    });
  },

  // @ts-expect-error TS2322
  streamedDesignSuggestions: null,
  setStreamedDesignSuggestions: (suggestions: PRDDesignSuggestions) => {
    set({ streamedDesignSuggestions: suggestions });
  },

  generatingDesignSuggestions: false,
  setGeneratingDesignSuggestions: (generating: boolean) => {
    set({ generatingDesignSuggestions: generating });
  },

  // @ts-expect-error TS2322
  generatingStoryboardIds: null,
  setGeneratingStoryboardIds: (generating: Set<string>) => {
    set({ generatingStoryboardIds: generating });
  },

  // @ts-expect-error TS2322
  aiChatEngSpecBlockIndex: null,
  aiChatEngSpecOpen: false,
  // @ts-expect-error TS2322
  aiChatEngSpecMessageId: null,
  openAiChatEngSpec: (blockIndex: number, messageId: string) => {
    set({
      aiChatEngSpecBlockIndex: blockIndex,
      aiChatEngSpecOpen: true,
      aiChatEngSpecMessageId: messageId,
    });
  },
  closeAiChatEngSpec: () => {
    set({
      // @ts-expect-error TS2322
      aiChatEngSpecBlockIndex: null,
      aiChatEngSpecOpen: false,
      // @ts-expect-error TS2322
      aiChatEngSpecMessageId: null,
    });
  },

  codeDiffPanelOpen: false,
  codeDiffMessageId: null,
  codeDiffToolCallId: null,
  openCodeDiffPanel: (toolCallId: string, messageId: string) => {
    set({
      codeDiffPanelOpen: true,
      codeDiffToolCallId: toolCallId,
      codeDiffMessageId: messageId,
    });
  },
  closeCodeDiffPanel: () => {
    set({
      codeDiffPanelOpen: false,
      codeDiffToolCallId: null,
      codeDiffMessageId: null,
    });
  },

  // @ts-expect-error TS2322
  aiChatSuggestions: null,
  setAiChatSuggestions: (suggestions: Suggestion[]) => {
    set({ aiChatSuggestions: suggestions });
  },

  triggerGenerateWireframes: false,
  setTriggerGenerateWireframes: (trigger: boolean) => {
    set({ triggerGenerateWireframes: trigger });
  },

  sendChatMessage: "",
  sendChatMessageFiles: undefined,
  refreshAfterResponse: false,
  includeErrorsIfAny: false,
  isFreePrompt: false,
  setSendChatMessage: ({
    sendChatMessage,
    sendChatMessageFiles,
    refreshAfterResponse,
    includeErrorsIfAny,
    isFreePrompt,
    isFixWithAi,
    resetToggles,
  }: SetSendChatMessageParams) => {
    const tempAssistantMessage = get().tempAssistantMessage;
    const { chatStreamingMessages } = useAiActionStore.getState();

    const isChatLoading = calculateIsChatLoading({
      chatStreamingMessages: chatStreamingMessages,
      tempAssistantMessage,
    });

    if (isChatLoading) {
      toast.info("Please wait for AI to finish the previous request");
      return false;
    }

    let numFixWithAiMessages = get().numFixWithAiMessages;
    if (isFixWithAi) {
      numFixWithAiMessages += 1;
    } else if (sendChatMessage) {
      // Clear if sending a non-fix with AI message
      // Otherwise keep the same as this function is also used
      // to clear the send chat message
      numFixWithAiMessages = 0;
    }

    set({
      sendChatMessage,
      sendChatMessageFiles,
      refreshAfterResponse,
      includeErrorsIfAny: includeErrorsIfAny || false,
      isFreePrompt: isFreePrompt || false,
      numFixWithAiMessages,
    });

    if (resetToggles === "all") {
      set({
        chatOnlyMode: false,
        useReasoning: false,
        useSearch: false,
      });
    } else if (resetToggles === "reasoning_and_search") {
      set({
        useReasoning: false,
        useSearch: false,
      });
    }

    return true;
  },
  resetToggles: (resetToggles?: "all" | "reasoning_and_search" | null) => {
    if (resetToggles === "all") {
      set({
        chatOnlyMode: false,
        useReasoning: false,
        useSearch: false,
      });
    } else if (resetToggles === "reasoning_and_search") {
      set({
        useReasoning: false,
        useSearch: false,
      });
    }
  },

  numFixWithAiMessages: 0,
  setNumFixWithAiMessages: (num: number) => {
    set({ numFixWithAiMessages: num });
  },

  // @ts-expect-error TS2322
  tempAssistantMessage: null,
  setTempAssistantMessage: (message: ManualAssistantMessage) => {
    set({ tempAssistantMessage: message });
  },

  // @ts-expect-error TS2322
  manualMessageToAdd: null,
  setAddManualMessage: (message: ManualAssistantMessage) => {
    set({ manualMessageToAdd: message });
  },

  setChatOnlyMode: (enabled: boolean) => {
    set({ chatOnlyMode: enabled });
  },

  setUseReasoning: (enabled: boolean) => {
    set({ useReasoning: enabled });
  },

  setUseSearch: (enabled: boolean) => {
    set({ useSearch: enabled });
  },
}));

export const useCurrentMessagesBeingShown = () => {
  const chatStreamingMessages = useAiActionStore(
    (state) => state.chatStreamingMessages
  );

  const isChatLoading = useIsChatLoading();

  // Manually adding an assistant message
  // Temporary - while streaming in
  const tempAssistantMessage = useAiContextStore(
    (state) => state.tempAssistantMessage
  );

  const { messages } = useCurrentConvo();

  const allErrors = useJoinedErrors();

  const withTempAssistantMessage = useMemo(() => {
    const baseMessages = chatStreamingMessages || messages || [];

    if (!tempAssistantMessage) return baseMessages;

    let fullTextContent = tempAssistantMessage?.text || "";
    if (tempAssistantMessage?.progressItems?.length) {
      fullTextContent +=
        '\n<action-progress>\n{"items":' +
        JSON.stringify(tempAssistantMessage?.progressItems) +
        "}\n</action-progress>";
    }
    if (tempAssistantMessage?.afterText) {
      fullTextContent += `\n${tempAssistantMessage?.afterText}`;
    }
    const newMessage = {
      ...tempAssistantMessage,
      content: fullTextContent,
    };
    return [...baseMessages, newMessage as any];
  }, [chatStreamingMessages, messages, tempAssistantMessage]);

  const withErrors = useMemo(() => {
    if (!allErrors?.length || isChatLoading) return withTempAssistantMessage;

    const withErrorsMessage = getAiMessageFromErrors(allErrors);

    return [...withTempAssistantMessage, withErrorsMessage];
  }, [withTempAssistantMessage, allErrors]);

  return withErrors;
};

export const getOpenedAiChatEngSpec = (
  messages: Message[],
  aiChatEngSpecBlockIndex: number,
  aiChatEngSpecMessageId: string
) => {
  const messageIdx = messages?.findIndex(
    (m) => m.id === aiChatEngSpecMessageId
  );

  if (
    !messages?.length ||
    aiChatEngSpecBlockIndex === null ||
    messageIdx === undefined
  )
    return null;

  const message = messages[messageIdx];

  const res = parseMessageContentBlocks(message);
  const block = res[aiChatEngSpecBlockIndex];

  if (block?.type === "aiChanges") {
    const aiChanges = block.aiChanges;

    // Only the last segment in last message is being streamed
    // Last segment is always guaranteed to be a content block
    const isStreaming =
      aiChatEngSpecBlockIndex === res.length - 1 &&
      messageIdx === messages.length - 1;

    return {
      commits: [
        {
          id: `change-from-chat-${message.id}`,
          feature: "Change made from chat",
          description: "",
          changes: aiChanges?.changes?.map((change, idx) => ({
            ...change,
            change: {
              ...(change?.change || {}),
              streaming: isStreaming,
            },
          })),
        },
      ],
    };
  }

  return null;
};

export const useOpenedAiChatEngSpec = () => {
  const messages = useCurrentMessagesBeingShown();

  const aiChatEngSpecMessageId = useAiContextStore(
    (state) => state.aiChatEngSpecMessageId
  );

  const aiChatEngSpecBlockIndex = useAiContextStore(
    (state) => state.aiChatEngSpecBlockIndex
  );

  return useMemo(() => {
    return getOpenedAiChatEngSpec(
      messages,
      aiChatEngSpecBlockIndex,
      aiChatEngSpecMessageId
    );
  }, [messages, aiChatEngSpecBlockIndex, aiChatEngSpecMessageId]);
};

/**
 * Hook to get code diff data from tool call results
 */
export const useCodeDiffData = () => {
  const codeDiffMessageId = useAiContextStore(
    (state) => state.codeDiffMessageId
  );
  const codeDiffToolCallId = useAiContextStore(
    (state) => state.codeDiffToolCallId
  );
  const messages = useCurrentMessagesBeingShown();
  const fileToContents = useCodebaseStore((state) => state.fileToContents);

  return useMemo(() => {
    if (!codeDiffMessageId || !codeDiffToolCallId || !messages) return null;

    const message = messages.find((msg) => msg.id === codeDiffMessageId);
    if (!message) return null;

    const segments = parseMessageContentBlocks(message);

    // Find the tool call segment with matching ID
    const toolCallSegment = segments.find(
      (segment) => segment.toolCall && segment.toolCallId === codeDiffToolCallId
    );

    if (!toolCallSegment?.toolCallResult) {
      return null;
    }

    const result = toolCallSegment.toolCallResult;

    const filename = result.filePath || "";
    const shortFileName = filename.split("/").pop() || filename;

    return {
      filename,
      shortFileName,
      oldContents: result.oldContents || "",
      newContents: fileToContents[filename] || "", // Read from current file state
      toolResult: result,
    };
  }, [codeDiffMessageId, codeDiffToolCallId, messages, fileToContents]);
};

export const useCodeDiffSetup = () => {
  const codeDiffPanelOpen = useAiContextStore(
    (state) => state.codeDiffPanelOpen
  );
  const codeDiffToolCallId = useAiContextStore(
    (state) => state.codeDiffToolCallId
  );
  const toolCallData = useCodeDiffData();
  const setEngSpecFiles = useCodebaseStore((state) => state.setEngSpecFiles);

  React.useEffect(() => {
    if (!toolCallData || !codeDiffPanelOpen) {
      // Clear any existing EngSpec files when closing diff panel
      setEngSpecFiles({ files: [] });
      return;
    }

    // Create the calculatedDiff for the Monaco editor
    const calculatedDiff = {
      filePath: toolCallData.filename,
      old: toolCallData.oldContents,
      new: toolCallData.newContents,
    };

    // Create an EditorFile for EngSpec context
    const editorFile = {
      id: `code-diff-${codeDiffToolCallId}`,
      filename: toolCallData.filename,
      keySuffix: "-code-diff",
      calculatedDiff: calculatedDiff,
      hardOpened: true,
      context: EditorContext.EngSpec,
      diffType: false,
      suggestedDiff: undefined,
    };

    // Set the file directly in EngSpec context (bypasses auto-processing)
    setEngSpecFiles({ files: [editorFile] });

    // Return cleanup function to clear files when effect dependencies change
    return () => {
      setEngSpecFiles({ files: [] });
    };
  }, [toolCallData, codeDiffPanelOpen, setEngSpecFiles, codeDiffToolCallId]);
};

/**
 * Opened file but with the most up-to-date streamed information
 */
export const useEngSpecOpenedFile = () => {
  const engSpecOpenedFileId = useAiContextStore(
    (state) => state.engSpecOpenedFileId
  );

  const codeDiffPanelOpen = useAiContextStore(
    (state) => state.codeDiffPanelOpen
  );
  const codeDiffMessageId = useAiContextStore(
    (state) => state.codeDiffMessageId
  );
  const codeDiffToolCallId = useAiContextStore(
    (state) => state.codeDiffToolCallId
  );

  const aiChatEngSpec = useOpenedAiChatEngSpec();
  const codeDiffData = useCodeDiffData();

  return useMemo(() => {
    // If code diff panel is open, provide code diff data
    if (codeDiffPanelOpen && codeDiffData) {
      const fileCommit = {
        id: `code-diff-${codeDiffToolCallId}`,
        path: codeDiffData.filename,
        change: {
          originalContents: codeDiffData.oldContents,
          newFullFileContent: codeDiffData.newContents,
        },
      };

      return {
        file: fileCommit,
        commit: {
          id: `code-diff-commit-${codeDiffMessageId}`,
          feature: "Change made from chat",
          description: "",
          changes: [fileCommit],
        },
        source: "chat",
      };
    }

    // Default EngSpec behavior
    let foundFile, foundCommit;
    aiChatEngSpec?.commits?.forEach((commit) => {
      if (foundFile) return;

      commit.changes?.forEach((file) => {
        if (foundFile) return;

        if (file.id === engSpecOpenedFileId) {
          foundFile = file;
          foundCommit = commit;
        }
      });
    });

    return {
      file: foundFile,
      commit: foundCommit,
      source: "chat",
    };
  }, [
    aiChatEngSpec,
    engSpecOpenedFileId,
    codeDiffPanelOpen,
    codeDiffData,
    codeDiffToolCallId,
    codeDiffMessageId,
  ]);
};

const calculateIsChatLoading = ({
  chatStreamingMessages,
  tempAssistantMessage,
}: {
  chatStreamingMessages: Message[] | null;
  tempAssistantMessage: ManualAssistantMessage;
}) => {
  return chatStreamingMessages != null || tempAssistantMessage != null;
};

export const useIsChatLoading = () => {
  const chatStreamingMessages = useAiActionStore(
    (state) => state.chatStreamingMessages
  );

  const tempAssistantMessage = useAiContextStore(
    (state) => state.tempAssistantMessage
  );

  return calculateIsChatLoading({
    chatStreamingMessages: chatStreamingMessages,
    tempAssistantMessage,
  });
};
